'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useResumeBuilderStore } from '@/stores/resume-builder-store';
import { Button } from '@/components/ui/forms/button';
import { Input } from '@/components/ui/forms/input';
import { Textarea } from '@/components/ui/forms/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/navigation/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from '@/components/ui/layout/dialog';
import { DialogHeading } from '@/components/ui/design-system';
import { Loader2, Save, ChevronDown, ChevronLeft, ChevronRight, Plus, Upload, FileText, Clock, Sparkles, Wand2, ArrowLeft, X, PenLine, Mic, Square, Share2, MoreVertical, Check, Eye, BarChart3, Linkedin, MessageSquareText } from 'lucide-react';
import type { GroupedSource } from '@/app/dashboard/components/GroupedSourcesPanel';
import { AddSourceDialog } from '@/components/features/sources/AddSourceDialog';
import { VoiceWaveform } from '@/components/features/voice/VoiceWaveform';
import { useToast } from '@/hooks/use-toast';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import ResumeForm from './components/ResumeForm';
import ResumePreview from './components/ResumePreview';
import StyleSelector from './components/StyleSelector';
import ResumeAnalyzeDialog from './components/ResumeAnalyzeDialog';
import ResumeVersionHistoryDialog from './components/ResumeVersionHistoryDialog';
import type { ResumeData } from '@/lib/resume/types';
import type { ResumeChangeItem } from '@/lib/resume/compare';
import { ResumeShareExportDialog } from '@/components/features/resume/ResumeShareExportDialog';
import { GuestResumeShareDialog } from '@/components/features/resume/GuestResumeShareDialog';
import { ResumeComparisonDialog } from '@/components/features/resume/ResumeComparisonDialog';
import { ResumeListItem, type ResumeListItemData } from '@/components/features/resume/ResumeListItem';
import MainLoader from '@/components/ui/feedback/MainLoader';
import usePaywallGuard from '@/hooks/usePaywallGuard';
import useAuthGuard from '@/hooks/useAuthGuard';
import { cn } from '@/lib/utils';
import { useResumeRefine } from '@/lib/resume/agent/refine/useResumeRefine';
import { ResumeRefineProgress } from '@/lib/resume/agent/refine/ResumeRefineProgress';
import { AutofillSourceDialog } from '@/components/features/resume/AutofillSourceDialog';
import { RefineJobSourceDialog } from '@/components/features/resume/RefineJobSourceDialog';
import { LinkedInFillDialog, type LinkedInResumeData } from './components/LinkedInFillDialog';
import { DEFAULT_RESUME_CONTENT, DEFAULT_RESUME_SETTINGS } from '@/lib/resume/types';
import { buildSharedResumePath } from '@/lib/resume/share';
import { ScoreRing } from '@/components/ui/data-display/score-ring';
import type { ResumeAnalysisSuggestion } from '@/lib/resume/analysis';

const GUEST_RESUME_STORAGE_KEY = 'co-guest-resume';
const GUEST_RESUME_ID = 'guest-local';
/** Anonymous resume access token — see `app/api/resume-builder/anonymous/route.ts`. */
const GUEST_TOKEN_STORAGE_KEY = 'co-guest-resume-token';

interface ResumeBuilderClientProps {
  resumeId?: string;
  /** When true the builder works without authentication. Server-dependent
   *  actions (export, AI tools, save, share, version history) show a sign-in
   *  dialog first and migrate the localStorage resume to the server. */
  guestMode?: boolean;
}

type StartMode = 'idle' | 'create';

/**
 * A finished before/after list waiting to be shown.
 *
 * Refine, improve and rollback all overwrite the stored resume in place, so
 * each route computes the comparison while it still holds both documents and
 * hands it back. The user's question is identical in all three cases — "what
 * did that just do to my resume?" — so one review dialog answers it, rather
 * than a dialog per flow with three slightly different empty states.
 */
interface ResumeChangeReview {
  title: string;
  description: string;
  changes: ResumeChangeItem[];
}

/**
 * The wire fields the refine route adds around the agent's own result.
 *
 * `useResumeRefine` types its result as the agent's `ResumeRefineResult`, which
 * knows nothing about them — and that hook belongs to the refine agent, so it
 * is read through this shape rather than widened.
 */
interface RefineWireExtras {
  changes?: ResumeChangeItem[];
  versionSnapshot?: { created?: boolean } | null;
}
const IMPROVE_WAVE_BAR_COUNT = 32;
const BUILDER_PAGE_SIZE = 3;
const IMPROVE_WAVE_IDLE_LEVEL = 0.2;

/**
 * The four AI tools, in the order a resume usually needs them: get content in,
 * point it at a job, tighten the wording, then check the result.
 *
 * Labels describe the OUTCOME. The old toolbar read "Autofill · Refine ·
 * Improve · Analyze", four verbs that gave no clue which one you wanted.
 */
type AiToolKey = 'autofill' | 'linkedin-fill' | 'refine' | 'improve' | 'analyze';

const AI_TOOLS: ReadonlyArray<{
  key: AiToolKey;
  label: string;
  description: string;
  icon: typeof Sparkles;
}> = [
  {
    key: 'autofill',
    label: 'Fill from a resume',
    description: 'Copy details across from a resume you already have.',
    icon: Sparkles,
  },
  {
    key: 'linkedin-fill',
    label: 'Fill from LinkedIn',
    description: 'Look up your LinkedIn profile and fill in your details.',
    icon: Linkedin,
  },
  {
    key: 'refine',
    label: 'Target a job',
    description: 'Rewrite this resume for one specific role.',
    icon: Wand2,
  },
  {
    key: 'improve',
    label: 'Polish the wording',
    description: 'Tighten the language without changing the facts.',
    icon: PenLine,
  },
  {
    key: 'analyze',
    label: 'Score and review',
    description: 'Rate the resume and suggest what to fix first.',
    icon: BarChart3,
  },
];

/**
 * Counts how many resume fields are meaningfully filled. Used to determine
 * whether to show "Fill in from" (mostly empty) vs "Analyze" (has content)
 * as the primary toolbar action.
 */
function countFilledResumeFields(resume: ResumeData | null): number {
  if (!resume?.content) return 0;
  const c = resume.content;
  let count = 0;
  if (c.personalInfo?.firstName?.trim()) count += 1;
  if (c.personalInfo?.lastName?.trim()) count += 1;
  if (c.profile?.headline?.trim()) count += 1;
  if (c.profile?.summary?.trim()) count += 1;
  if (c.contact?.location?.trim()) count += 1;
  if (c.employment?.length > 0) count += 1;
  if (c.education?.length > 0) count += 1;
  if (c.skills?.technical?.length > 0 || c.skills?.soft?.length > 0) count += 1;
  return count;
}

export default function ResumeBuilderClient({ resumeId, guestMode }: ResumeBuilderClientProps) {
  const AUTOSAVE_FALLBACK_MS = 60_000;
  const router = useRouter();
  const searchParams = useSearchParams();
  const openMode = searchParams.get('mode');
  const autoStartParam = searchParams.get('autostart');
  const analysisReportIdParam = searchParams.get('analysisReportId');
  const isRefinePreparing = openMode === 'refine-preparing';
  const shouldOpenRefineFlow = openMode === 'refine';
  const shouldOpenTailorFlow = openMode === 'tailor';
  const shouldOpenImproveFlow = openMode === 'improve';
  const shouldAutoStartRefine =
    shouldOpenRefineFlow &&
    (autoStartParam === '1' || autoStartParam?.toLowerCase() === 'true');
  const shouldShowRefineHint = shouldOpenRefineFlow && Boolean(resumeId);
  const { toast } = useToast();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const {
    resume,
    isLoading,
    isSaving,
    hasUnsavedChanges,
    lastSaved,
    error,
    loadResume,
    saveResume,
    createVersion,
    setResume,
    updateContent,
    updateEmployment,
    updateCustomSection,
    updateShare,
    updateName,
    guestSyncToken,
    setGuestSyncToken,
  } = useResumeBuilderStore();

  // ---------------------------------------------------------------------------
  // Guest mode: auth guard + localStorage sync + server migration
  // ---------------------------------------------------------------------------
  const guestAuth = useAuthGuard({
    signInTitle: 'Sign in to unlock this feature',
    signInDescription:
      'Create a free account to save, export, and use AI tools on your resume.',
  });

  /**
   * Gets the current guest resume onto the server under the now-signed-in
   * user's account, returning the new (or reassigned) server ID.
   *
   * If Share & Export already minted a server-side anonymous resume (see
   * `app/api/resume-builder/anonymous/route.ts`), this claims that SAME row
   * — reassigning it rather than creating a duplicate, so the resume the
   * guest already shared keeps its id and history. Only falls back to a
   * plain create for a guest who never triggered an anonymous save.
   */
  const migrateGuestResume = useCallback(async (): Promise<string | null> => {
    const currentResume = useResumeBuilderStore.getState().resume;
    if (!currentResume) return null;

    const clearGuestStorage = () => {
      try {
        localStorage.removeItem(GUEST_RESUME_STORAGE_KEY);
        localStorage.removeItem(GUEST_TOKEN_STORAGE_KEY);
      } catch { /* localStorage unavailable */ }
    };

    let existingToken: string | null = useResumeBuilderStore.getState().guestSyncToken;
    if (!existingToken) {
      try { existingToken = localStorage.getItem(GUEST_TOKEN_STORAGE_KEY); } catch { existingToken = null; }
    }

    try {
      if (existingToken) {
        const claimResponse = await fetch('/api/resume-builder/anonymous', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: existingToken }),
        });

        if (claimResponse.ok) {
          const data = await claimResponse.json();
          clearGuestStorage();
          return data.resume?.id ?? null;
        }
        // Token no longer valid server-side (expired/cleaned up) — fall
        // through to a plain create below instead of leaving the guest stuck.
      }

      const response = await fetch('/api/resume-builder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: currentResume.name || 'Untitled Resume',
          content: currentResume.content,
          settings: currentResume.settings,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        clearGuestStorage();
        return data.resume?.id ?? null;
      }
    } catch (error) {
      console.error('Failed to migrate guest resume:', error);
    }
    return null;
  }, []);

  /**
   * Wrap any server-dependent action. In guest mode it shows the sign-in
   * dialog, migrates the resume, and redirects to the authenticated builder.
   * Outside guest mode it runs the action directly.
   */
  const withGuestGate = useCallback(
    (action?: () => void) => {
      if (!guestMode) {
        action?.();
        return;
      }

      guestAuth.handleAction(async () => {
        const migratedId = await migrateGuestResume();
        if (migratedId) {
          router.push(`/d/pitch/resumes/builder/${migratedId}`);
        }
      }, 'sign-up');
    },
    [guestMode, guestAuth, migrateGuestResume, router],
  );

  const [isPreparingGuestShare, setIsPreparingGuestShare] = useState(false);

  /**
   * Guest "Share & export": ensures a server-side anonymous resume exists
   * (creating one on first use, or syncing the latest edits into an existing
   * one) before opening the share dialog, since the dialog's link and export
   * buttons only work once that server copy exists — see
   * `app/api/resume-builder/anonymous/route.ts`.
   */
  const handleGuestShare = useCallback(async () => {
    const currentResume = useResumeBuilderStore.getState().resume;
    if (!currentResume) return;

    setIsPreparingGuestShare(true);
    try {
      const existingToken = useResumeBuilderStore.getState().guestSyncToken;

      if (existingToken) {
        const syncResponse = await fetch('/api/resume-builder/anonymous', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            token: existingToken,
            name: currentResume.name,
            content: currentResume.content,
            settings: currentResume.settings,
          }),
        });

        if (syncResponse.ok) {
          setShowShareDialog(true);
          return;
        }

        // Dead token (expired/cleaned up server-side) — clear it and mint a
        // fresh one below instead of leaving the guest stuck.
        setGuestSyncToken(null);
        try { localStorage.removeItem(GUEST_TOKEN_STORAGE_KEY); } catch {}
      }

      const createResponse = await fetch('/api/resume-builder/anonymous', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: currentResume.name || 'Untitled Resume',
          content: currentResume.content,
          settings: currentResume.settings,
        }),
      });

      if (!createResponse.ok) {
        const errorPayload = await createResponse.json().catch(() => null);
        toast({
          title: 'Could not create share link',
          description: errorPayload?.error || 'Something went wrong. Please try again.',
          variant: 'destructive',
        });
        return;
      }

      const data = await createResponse.json();
      if (!data.token) {
        throw new Error('Missing token in response');
      }

      setGuestSyncToken(data.token);
      try { localStorage.setItem(GUEST_TOKEN_STORAGE_KEY, data.token); } catch {}
      setShowShareDialog(true);
    } catch (error) {
      console.error('Failed to prepare guest share:', error);
      toast({
        title: 'Could not create share link',
        description: 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsPreparingGuestShare(false);
    }
  }, [setGuestSyncToken, toast]);

  // Guest mode: initialise the store from localStorage or blank defaults.
  useEffect(() => {
    if (!guestMode) return;

    let initial: ResumeData | null = null;
    try {
      const raw = localStorage.getItem(GUEST_RESUME_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        initial = {
          ...parsed,
          id: GUEST_RESUME_ID,
          createdAt: new Date(parsed.createdAt),
          updatedAt: new Date(parsed.updatedAt),
        };
      }
    } catch { /* corrupt or missing — start fresh */ }

    if (!initial) {
      initial = {
        id: GUEST_RESUME_ID,
        name: 'Untitled Resume',
        version: 1,
        status: 'DRAFT',
        content: DEFAULT_RESUME_CONTENT,
        settings: DEFAULT_RESUME_SETTINGS,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
    }

    // Set synchronously first so guest mode never shows a loading gate (see
    // the isLoading check below) — a resume previously saved to the server
    // then takes over from this localStorage snapshot once it loads, so a
    // returning guest always sees their latest saved version rather than a
    // stale local copy.
    setResume(initial);

    let token: string | null = null;
    try {
      token = localStorage.getItem(GUEST_TOKEN_STORAGE_KEY);
    } catch { /* localStorage unavailable */ }

    if (!token) return;

    setGuestSyncToken(token);

    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`/api/resume-builder/anonymous?token=${encodeURIComponent(token!)}`);
        if (!response.ok) {
          if (response.status === 404) {
            // Expired or deleted server-side — stop treating this browser as
            // having a live server copy, but keep the local snapshot so the
            // guest's work is not lost.
            try { localStorage.removeItem(GUEST_TOKEN_STORAGE_KEY); } catch {}
            setGuestSyncToken(null);
          }
          return;
        }

        const data = await response.json();
        if (cancelled || !data.resume) return;

        const serverResume: ResumeData = {
          ...data.resume,
          id: GUEST_RESUME_ID,
          createdAt: new Date(data.resume.createdAt),
          updatedAt: new Date(data.resume.updatedAt),
        };
        setResume(serverResume);
        try {
          localStorage.setItem(GUEST_RESUME_STORAGE_KEY, JSON.stringify(serverResume));
        } catch { /* localStorage full or unavailable */ }
      } catch (error) {
        console.error('Failed to load guest resume from server:', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [guestMode, setResume, setGuestSyncToken]);

  // Guest mode: persist resume changes to localStorage (debounced 500ms).
  useEffect(() => {
    if (!guestMode || !resume || !resume.id.startsWith('guest-')) return;
    const timer = setTimeout(() => {
      try {
        localStorage.setItem(GUEST_RESUME_STORAGE_KEY, JSON.stringify(resume));
      } catch { /* localStorage full or unavailable */ }
    }, 500);
    return () => clearTimeout(timer);
  }, [guestMode, resume]);

  const [showNewResumeDialog, setShowNewResumeDialog] = useState(!resumeId && !isRefinePreparing && !guestMode);
  const [newResumeName, setNewResumeName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showMobilePreviewDialog, setShowMobilePreviewDialog] = useState(false);
  const [showMobileMenuDialog, setShowMobileMenuDialog] = useState(false);
  const [resumes, setResumes] = useState<ResumeData[]>([]);
  const [loadingResumes, setLoadingResumes] = useState(true);
  // Autofill and refine each get their own single-purpose dialog. The old shared
  // "sources" dialog made the user pick an action, then a source type, then a
  // source — three steps to express one intent.
  const [showAutofillDialog, setShowAutofillDialog] = useState(false);
  const [showLinkedInFillDialog, setShowLinkedInFillDialog] = useState(false);
  const [showRefineDialog, setShowRefineDialog] = useState(false);
  const [showAddSourceDialog, setShowAddSourceDialog] = useState(false);
  const [addSourceInitialTab, setAddSourceInitialTab] = useState<'resume' | 'profile' | 'note' | 'job' | 'company'>('resume');
  const [sources, setSources] = useState<GroupedSource[]>([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const [isFillingFromSources, setIsFillingFromSources] = useState(false);
  const [isRefining, setIsRefining] = useState(false);
  /**
   * Streams the refine agent's steps. `isRefining` stays as the busy flag that
   * the surrounding buttons already gate on; this only adds the progress view.
   */
  const refineAgent = useResumeRefine(resumeId ?? '');
  const [showImproveDialog, setShowImproveDialog] = useState(false);
  /**
   * The last rewrite's before/after list, from whichever flow produced it.
   * Nothing on the client can reconstruct one after the fact — the previous
   * content is gone the moment the row is updated.
   */
  const [changeReview, setChangeReview] = useState<ResumeChangeReview | null>(null);
  const [showVersionHistoryDialog, setShowVersionHistoryDialog] = useState(false);
  const [showAnalyzeDialog, setShowAnalyzeDialog] = useState(false);
  const [latestAnalysisScore, setLatestAnalysisScore] = useState<number | null>(null);
  const [isLoadingAnalysisScore, setIsLoadingAnalysisScore] = useState(false);
  const [isAnalysisInProgress, setIsAnalysisInProgress] = useState(false);
  const [analysisSuggestions, setAnalysisSuggestions] = useState<ResumeAnalysisSuggestion[]>([]);
  const [showAnalysisComments, setShowAnalysisComments] = useState(false);
  const [applyingSuggestionId, setApplyingSuggestionId] = useState<string | null>(null);
  const [isImproving, setIsImproving] = useState(false);
  const [improveInstructions, setImproveInstructions] = useState('');
  const [isImproveVoiceRecording, setIsImproveVoiceRecording] = useState(false);
  const [isImproveVoiceTranscribing, setIsImproveVoiceTranscribing] = useState(false);
  const [improveVoiceError, setImproveVoiceError] = useState<string | null>(null);
  const [improveVoiceLevels, setImproveVoiceLevels] = useState<number[]>(
    Array.from({ length: IMPROVE_WAVE_BAR_COUNT }, () => IMPROVE_WAVE_IDLE_LEVEL)
  );
  const [emptyStateMode, setEmptyStateMode] = useState<StartMode>('idle');
  const [emptyStatePage, setEmptyStatePage] = useState(0);
  const [dialogStartMode, setDialogStartMode] = useState<StartMode>('idle');
  const [dialogPage, setDialogPage] = useState(0);
  const { handleAction: handleAnalyzePaywallAction, isPaywallActionInProgress: isAnalyzeGuardBusy } =
    usePaywallGuard({
      signInFeatureSlug: 'resume-analysis',
      paywallTitle: 'See exactly what\'s costing you replies — section by section.',
      paywallDescription:
        'A real review of your resume against the role. You get the score, the weak points, and a fix path you can apply in place.',
      showFullPageLoaderOverlay: false,
    });
  const hasOpenedRefineFlowRef = useRef(false);
  const hasAutoStartedRefineRef = useRef(false);
  const hasOpenedTailorFlowRef = useRef(false);
  const hasOpenedImproveFlowRef = useRef(false);
  const improveVoiceRecorderRef = useRef<MediaRecorder | null>(null);
  const improveVoiceStreamRef = useRef<MediaStream | null>(null);
  const improveVoiceChunksRef = useRef<Blob[]>([]);
  const cancelImproveVoiceRef = useRef(false);
  const improveVoiceAudioContextRef = useRef<AudioContext | null>(null);
  const improveVoiceAnalyserRef = useRef<AnalyserNode | null>(null);
  const improveVoiceAnimationRef = useRef<number | null>(null);
  const unsavedChangesStartedAtRef = useRef<number | null>(null);
  const [, setAutosaveFallbackTick] = useState(0);

  // Load existing resumes (skip in guest mode — no server data)
  useEffect(() => {
    if (guestMode) {
      setLoadingResumes(false);
      return;
    }
    async function fetchResumes() {
      try {
        const response = await fetch('/api/resume-builder');
        if (response.ok) {
          const data = await response.json();
          setResumes(data.resumes || []);
        }
      } catch (error) {
        console.error('Failed to load resumes:', error);
      } finally {
        setLoadingResumes(false);
      }
    }
    fetchResumes();
  }, [guestMode]);

  // Load resume if ID provided (skip in guest mode — data comes from localStorage)
  useEffect(() => {
    if (guestMode) return;
    if (resumeId) {
      loadResume(resumeId);
    }
  }, [guestMode, resumeId, loadResume]);

  /**
   * Latest completed analysis score for the toolbar indicator. Reports are
   * newest-first, so the first COMPLETED one with a score is the current
   * one — a resume can accumulate several analysis runs over time.
   *
   * Returns a cancel function so callers tied to an effect can ignore a
   * stale response that resolves after the resume id has already changed.
   */
  const refreshAnalysisScore = useCallback((id: string) => {
    let cancelled = false;
    setIsLoadingAnalysisScore(true);
    fetch(`/api/resume-builder/${id}/analysis-reports`)
      .then((res) => (res.ok ? res.json() : null))
      .then(async (data) => {
        if (cancelled) return;
        const reports = data?.reports ?? [];

        const inProgress = reports.some(
          (r: any) => r.generation_status === 'PENDING' || r.generation_status === 'RUNNING'
        );
        setIsAnalysisInProgress(inProgress);

        const latest = reports.find(
          (r: any) => r.generation_status === 'COMPLETED' && r.content?.overall?.score != null
        );
        setLatestAnalysisScore(latest?.content?.overall?.score ?? null);

        const latestReportId = latest?.id;
        if (latestReportId) {
          const detailRes = await fetch(`/api/resume-builder/${id}/analysis-reports/${latestReportId}`);
          if (cancelled) return;
          if (detailRes.ok) {
            const detailData = await detailRes.json();
            const suggestions = detailData?.report?.content?.suggestions;
            if (Array.isArray(suggestions)) {
              setAnalysisSuggestions(suggestions);
              setShowAnalysisComments(suggestions.length > 0);
            }
          }
        }
      })
      .catch(() => {
        if (!cancelled) setLatestAnalysisScore(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingAnalysisScore(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!resume?.id || resume.id.startsWith('guest-')) {
      setLatestAnalysisScore(null);
      return;
    }
    return refreshAnalysisScore(resume.id);
  }, [resume?.id, refreshAnalysisScore]);

  useEffect(() => {
    if (isRefinePreparing) {
      setShowNewResumeDialog(false);
    }
  }, [isRefinePreparing]);

  useEffect(() => {
    if (!showNewResumeDialog) {
      setDialogStartMode('idle');
      setDialogPage(0);
      setNewResumeName('');
    }
  }, [showNewResumeDialog]);

  const refreshSources = useCallback(async () => {
    if (!resumeId) {
      setSources([]);
      return;
    }

    setLoadingSources(true);
    try {
      const response = await fetch(`/api/resume-builder/${resumeId}/sources`);
      if (response.ok) {
        const data = await response.json();
        setSources(data.sources || []);
      }
    } catch (error) {
      console.error('Failed to load sources:', error);
    } finally {
      setLoadingSources(false);
    }
  }, [resumeId]);

  // Load sources when resume changes
  useEffect(() => {
    void refreshSources();
  }, [refreshSources]);

  // Handle creating a new resume
  const handleCreateResume = async () => {
    if (!newResumeName.trim()) return;

    setIsCreating(true);
    try {
      const response = await fetch('/api/resume-builder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newResumeName.trim() }),
      });

      if (response.ok) {
        const data = await response.json();
        setShowNewResumeDialog(false);
        setNewResumeName('');
        router.push(`/d/pitch/resumes/builder/${data.resume.id}`);
      }
    } catch (error) {
      console.error('Failed to create resume:', error);
    } finally {
      setIsCreating(false);
    }
  };

  // Handle selecting an existing resume
  const handleSelectResume = (id: string) => {
    setShowNewResumeDialog(false);
    router.push(`/d/pitch/resumes/builder/${id}`);
  };

  // Handle save as new version
  const handleSaveAsVersion = async () => {
    const newResume = await createVersion();
    if (newResume) {
      router.push(`/d/pitch/resumes/builder/${newResume.id}`);
    }
  };

  const handleViewOriginal = () => {
    if (!resume?.originalSourceDownloadUrl) {
      toast({
        title: 'Original not available',
        description: 'Could not find a linked original upload for this resume.',
        variant: 'destructive',
      });
      return;
    }

    window.open(resume.originalSourceDownloadUrl, '_blank', 'noopener,noreferrer');
  };

  const currentResumeId = resume?.id ?? resumeId ?? null;
  const orderedResumes = useMemo(() => {
    if (resumes.length === 0 || !currentResumeId) return resumes;

    const currentResume = resumes.find((item) => item.id === currentResumeId);
    if (!currentResume) return resumes;

    const otherResumes = resumes.filter((item) => item.id !== currentResumeId);
    return [currentResume, ...otherResumes];
  }, [currentResumeId, resumes]);

  const builderResumeItems: ResumeListItemData[] = useMemo(
    () =>
      orderedResumes.map((r) => ({
        id: r.id,
        title: r.name || 'Untitled Resume',
        fileName: r.version > 1 ? `v${r.version}` : 'Builder Resume',
        fileSize: 0,
        fileMime: 'application/json',
        createdAt: r.createdAt,
        updatedAt: r.updatedAt,
        sourceType: 'builder' as const,
      })),
    [orderedResumes]
  );

  const emptyStateTotalPages = Math.max(1, Math.ceil(builderResumeItems.length / BUILDER_PAGE_SIZE));
  const emptyStateClampedPage = Math.min(emptyStatePage, emptyStateTotalPages - 1);
  const emptyStateVisible = useMemo(
    () => builderResumeItems.slice(emptyStateClampedPage * BUILDER_PAGE_SIZE, (emptyStateClampedPage + 1) * BUILDER_PAGE_SIZE),
    [builderResumeItems, emptyStateClampedPage]
  );

  const dialogTotalPages = Math.max(1, Math.ceil(builderResumeItems.length / BUILDER_PAGE_SIZE));
  const dialogClampedPage = Math.min(dialogPage, dialogTotalPages - 1);
  const dialogVisible = useMemo(
    () => builderResumeItems.slice(dialogClampedPage * BUILDER_PAGE_SIZE, (dialogClampedPage + 1) * BUILDER_PAGE_SIZE),
    [builderResumeItems, dialogClampedPage]
  );

  const isActionBusy = isFillingFromSources || isRefining || isImproving;

  const handleOpenAnalyzeDialog = useCallback(() => {
    if (guestMode) { withGuestGate(); return; }
    void handleAnalyzePaywallAction(() => {
      setShowAnalyzeDialog(true);
    }, 'sign-in');
  }, [guestMode, handleAnalyzePaywallAction, withGuestGate]);

  /** Single entry point for the AI tools menu. In guest mode most tools are
   *  gated behind sign-in + migration since they need a server resume.
   *  LinkedIn fill works without auth — it only writes to the local store. */
  const openAiTool = useCallback(
    (key: AiToolKey) => {
      // LinkedIn fill works in guest mode — no server resume required.
      if (key === 'linkedin-fill') { setShowLinkedInFillDialog(true); return; }
      if (guestMode) { withGuestGate(); return; }
      if (key === 'autofill') setShowAutofillDialog(true);
      else if (key === 'refine') setShowRefineDialog(true);
      else if (key === 'improve') setShowImproveDialog(true);
      else void handleOpenAnalyzeDialog();
    },
    [guestMode, handleOpenAnalyzeDialog, withGuestGate]
  );

  /** Any tool mid-run — drives the single spinner on the menu trigger. */
  const isAiToolRunning = isFillingFromSources || isRefining || isImproving || isAnalyzeGuardBusy;

  // Format last saved time
  const formatLastSaved = () => {
    if (!lastSaved) return '';
    const diff = Date.now() - lastSaved.getTime();
    if (diff < 60000) return 'Saved just now';
    if (diff < 3600000) return `Saved ${Math.floor(diff / 60000)} min ago`;
    return `Saved at ${lastSaved.toLocaleTimeString()}`;
  };

  useEffect(() => {
    if (hasUnsavedChanges) {
      unsavedChangesStartedAtRef.current ??= Date.now();
      const intervalId = window.setInterval(() => {
        setAutosaveFallbackTick((current) => current + 1);
      }, 5000);
      return () => window.clearInterval(intervalId);
    }

    unsavedChangesStartedAtRef.current = null;
    setAutosaveFallbackTick((current) => current + 1);
    return undefined;
  }, [hasUnsavedChanges]);

  const shouldShowManualSaveFallback = Boolean(
    hasUnsavedChanges &&
      unsavedChangesStartedAtRef.current &&
      Date.now() - unsavedChangesStartedAtRef.current >= AUTOSAVE_FALLBACK_MS
  );

  // Handle adding a source

  // Handle submitting a new source
  const handleSubmitSource = async (sourceData: Record<string, unknown>): Promise<boolean> => {
    if (!resumeId) return false;

    try {
      const response = await fetch(`/api/resume-builder/${resumeId}/sources`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sourceData),
      });

      if (response.ok) {
        const data = await response.json();
        setSources((prev) => [data.source, ...prev]);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to add source:', error);
      return false;
    }
  };

  // Handle deleting a source


  // Handle fill from sources (AI autofill)
  const runFillFromSources = useCallback(async (): Promise<boolean> => {
    if (!resumeId || sources.length === 0) {
      toast({
        title: 'No sources',
        description: 'Add sources first to autofill your resume.',
        variant: 'destructive',
      });
      return false;
    }

    setIsFillingFromSources(true);
    try {
      const response = await fetch(`/api/resume-builder/${resumeId}/fill-from-sources`, {
        method: 'POST',
      });

      if (response.ok) {
        await response.json();
        // Reload the resume to show updated content
        await loadResume(resumeId);
        toast({
          title: 'Success!',
          description: 'Resume autofilled from sources.',
        });
        return true;
      } else {
        const error = await response.json();
        toast({
          title: 'Error',
          description: error.error || 'Failed to autofill resume',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Failed to fill from sources:', error);
      toast({
        title: 'Error',
        description: 'An error occurred while autofilling',
        variant: 'destructive',
      });
    } finally {
      setIsFillingFromSources(false);
    }
    return false;
  }, [loadResume, resumeId, sources.length, toast]);

  // Handle fill from LinkedIn profile data.
  // Uses `updateContent` so the change is marked unsaved and autosave kicks in.
  const handleLinkedInFill = useCallback(
    (data: LinkedInResumeData) => {
      if (!resume) return;

      updateContent({
        personalInfo: {
          firstName: data.firstName || resume.content.personalInfo.firstName,
          lastName: data.lastName || resume.content.personalInfo.lastName,
        },
        profile: {
          headline: data.headline || resume.content.profile.headline,
          summary: data.summary || resume.content.profile.summary,
        },
        contact: {
          location: data.location || resume.content.contact.location,
        },
        employment:
          data.employment.length > 0
            ? data.employment
            : resume.content.employment,
        education:
          data.education.length > 0
            ? data.education
            : resume.content.education,
        skills:
          data.skills.length > 0
            ? { ...resume.content.skills, technical: data.skills }
            : resume.content.skills,
      });

      toast({
        title: 'LinkedIn profile imported',
        description: 'Your resume has been filled with LinkedIn details.',
      });
    },
    [resume, toast, updateContent],
  );

  // Handle refine based on sources
  const runRefine = useCallback(async (): Promise<boolean> => {
    if (!resumeId || sources.length === 0) {
      toast({
        title: 'No sources',
        description: 'Add sources first to refine your resume.',
        variant: 'destructive',
      });
      return false;
    }

    setIsRefining(true);
    try {
      const result = await refineAgent.refine();
      if (result) {
        // Reload the resume to show refined content
        await loadResume(resumeId);

        /**
         * Refine rewrites prose across the whole document and the editor simply
         * repaints, so without this the user is left comparing the resume
         * against their memory of it. Improve has always shown a comparison
         * here; refine returning one is what makes the two flows match.
         */
        const extras = result as unknown as RefineWireExtras;
        const changes = Array.isArray(extras.changes) ? extras.changes : [];
        const snapshotted = extras.versionSnapshot?.created === true;

        if (changes.length > 0) {
          setChangeReview({
            title: 'What refine changed',
            description: snapshotted
              ? 'The refined resume is saved and loaded in the editor. The version before this run is in version history.'
              : 'The refined resume is saved and loaded in the editor. This is what changed.',
            changes,
          });
        }

        toast({
          title: 'Success!',
          description: snapshotted
            ? 'Resume refined for target positions. The previous version was saved to history.'
            : 'Resume refined for target positions.',
        });
        return true;
      }
      toast({
        title: 'Error',
        description: refineAgent.error ?? 'Failed to refine resume',
        variant: 'destructive',
      });
    } catch (error) {
      console.error('Failed to refine resume:', error);
      toast({
        title: 'Error',
        description: 'An error occurred while refining',
        variant: 'destructive',
      });
    } finally {
      setIsRefining(false);
    }
    return false;
  }, [loadResume, refineAgent, resumeId, sources.length, toast]);

  useEffect(() => {
    hasOpenedRefineFlowRef.current = false;
    hasAutoStartedRefineRef.current = false;
    hasOpenedTailorFlowRef.current = false;
    hasOpenedImproveFlowRef.current = false;
  }, [resumeId, shouldAutoStartRefine, shouldOpenRefineFlow, shouldOpenTailorFlow, shouldOpenImproveFlow]);

  useEffect(() => {
    if (!resumeId || !shouldOpenRefineFlow || hasOpenedRefineFlowRef.current) {
      return;
    }
    hasOpenedRefineFlowRef.current = true;
    setShowRefineDialog(true);
  }, [resumeId, shouldOpenRefineFlow]);

  useEffect(() => {
    if (
      !resumeId ||
      !shouldAutoStartRefine ||
      hasAutoStartedRefineRef.current ||
      loadingSources ||
      isRefining ||
      sources.length === 0
    ) {
      return;
    }

    hasAutoStartedRefineRef.current = true;
    setShowRefineDialog(true);

    void (async () => {
      const success = await runRefine();
      if (success) {
        setShowRefineDialog(false);
      }
    })();
  }, [isRefining, loadingSources, resumeId, runRefine, shouldAutoStartRefine, sources.length]);

  useEffect(() => {
    if (!resumeId || !shouldOpenTailorFlow || hasOpenedTailorFlowRef.current) return;
    hasOpenedTailorFlowRef.current = true;
    setAddSourceInitialTab('job');
    setShowAddSourceDialog(true);
  }, [resumeId, shouldOpenTailorFlow]);

  useEffect(() => {
    if (!resumeId || !shouldOpenImproveFlow || hasOpenedImproveFlowRef.current) return;
    hasOpenedImproveFlowRef.current = true;
    setShowImproveDialog(true);
  }, [resumeId, shouldOpenImproveFlow]);

  useEffect(() => {
    if (!resumeId || !analysisReportIdParam) return;
    setShowAnalyzeDialog(true);
  }, [resumeId, analysisReportIdParam]);

  const resetImproveVoiceLevels = useCallback(() => {
    setImproveVoiceLevels(Array.from({ length: IMPROVE_WAVE_BAR_COUNT }, () => IMPROVE_WAVE_IDLE_LEVEL));
  }, []);

  const cleanupImproveVoiceVisualization = useCallback(() => {
    if (improveVoiceAnimationRef.current) {
      cancelAnimationFrame(improveVoiceAnimationRef.current);
      improveVoiceAnimationRef.current = null;
    }
    if (improveVoiceAudioContextRef.current) {
      improveVoiceAudioContextRef.current.close().catch(() => undefined);
      improveVoiceAudioContextRef.current = null;
    }
    improveVoiceAnalyserRef.current = null;
    resetImproveVoiceLevels();
  }, [resetImproveVoiceLevels]);

  const stopImproveVoiceRecording = (cancel: boolean) => {
    cancelImproveVoiceRef.current = cancel;
    if (improveVoiceRecorderRef.current?.state === 'recording') {
      improveVoiceRecorderRef.current.stop();
    }
    if (cancel) {
      setIsImproveVoiceRecording(false);
      improveVoiceChunksRef.current = [];
      cleanupImproveVoiceVisualization();
      if (improveVoiceStreamRef.current) {
        improveVoiceStreamRef.current.getTracks().forEach((track) => track.stop());
        improveVoiceStreamRef.current = null;
      }
    }
  };

  const handleImproveDialogOpenChange = (open: boolean) => {
    if (!open) {
      if (isImproving || isImproveVoiceTranscribing) {
        return;
      }
      if (improveVoiceRecorderRef.current?.state === 'recording') {
        stopImproveVoiceRecording(true);
      }
      setImproveInstructions('');
      setImproveVoiceError(null);
      setShowImproveDialog(false);
      return;
    }

    setShowImproveDialog(true);
  };

  const handleStartImproveVoiceRecording = async () => {
    if (!resumeId || isImproveVoiceRecording || isImproveVoiceTranscribing || isImproving) return;

    setImproveVoiceError(null);
    improveVoiceChunksRef.current = [];
    cancelImproveVoiceRef.current = false;

    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setImproveVoiceError('Voice recording is not supported in this browser.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      improveVoiceStreamRef.current = stream;
      const recorder = new MediaRecorder(stream);
      improveVoiceRecorderRef.current = recorder;
      resetImproveVoiceLevels();

      try {
        const AudioCtx =
          window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!AudioCtx) {
          throw new Error('AudioContext not supported');
        }
        const audioContext = new AudioCtx();
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 1024;
        analyser.smoothingTimeConstant = 0.45;
        source.connect(analyser);
        improveVoiceAudioContextRef.current = audioContext;
        improveVoiceAnalyserRef.current = analyser;

        /*
         * A scrolling loudness meter, not a spectrum.
         *
         * This used to map bars onto frequency bins, which is why it never
         * tracked speech: bars 0-2 covered ~0-150Hz (DC offset, mic rumble, room
         * noise) so they were permanently tall, while the exponent pushed the
         * remaining bars up to ~20-24kHz where speech has no energy at all and
         * the mic has rolled off — those sat on the floor no matter what was
         * said. It also added Math.random() jitter, which faked motion that had
         * nothing to do with the input.
         *
         * Each bar is now the RMS amplitude of one moment, newest on the right,
         * so the bars ARE the shape of what was just said.
         */
        const timeData = new Uint8Array(analyser.fftSize);
        let lastSampleAt = 0;

        const updateLevels = (now: number) => {
          if (!improveVoiceAnalyserRef.current) return;

          // ~20fps: at 60 the 32 bars would scroll past in half a second, too
          // fast to read as speech. This holds roughly 1.6s of history.
          if (now - lastSampleAt >= 50) {
            lastSampleAt = now;
            improveVoiceAnalyserRef.current.getByteTimeDomainData(timeData);

            let sumSquares = 0;
            for (let i = 0; i < timeData.length; i += 1) {
              // Byte time-domain data is centred on 128; shift to -1..1.
              const amplitude = (timeData[i] - 128) / 128;
              sumSquares += amplitude * amplitude;
            }
            const rms = Math.sqrt(sumSquares / timeData.length);

            // Conversational speech sits around 0.05-0.2 RMS, so raw values
            // would barely leave the floor. Gain then compress, so a normal
            // voice uses most of the height and a shout still fits.
            const level = Math.min(1, Math.pow(rms * 4, 0.65));

            setImproveVoiceLevels((prev) => {
              const next = prev.length === IMPROVE_WAVE_BAR_COUNT
                ? prev.slice(1)
                : prev.slice(Math.max(0, prev.length - IMPROVE_WAVE_BAR_COUNT + 1));
              return [...next, Math.max(IMPROVE_WAVE_IDLE_LEVEL, level)];
            });
          }

          improveVoiceAnimationRef.current = requestAnimationFrame(updateLevels);
        };

        if (improveVoiceAnimationRef.current) {
          cancelAnimationFrame(improveVoiceAnimationRef.current);
        }
        improveVoiceAnimationRef.current = requestAnimationFrame(updateLevels);
      } catch (error) {
        console.warn('Improve voice visualization setup failed:', error);
      }

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          improveVoiceChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        setIsImproveVoiceRecording(false);
        cleanupImproveVoiceVisualization();
        stream.getTracks().forEach((track) => track.stop());
        improveVoiceStreamRef.current = null;

        if (cancelImproveVoiceRef.current) {
          cancelImproveVoiceRef.current = false;
          improveVoiceChunksRef.current = [];
          return;
        }

        const blob = new Blob(improveVoiceChunksRef.current, {
          type: recorder.mimeType || 'audio/webm',
        });
        improveVoiceChunksRef.current = [];

        if (!blob.size) {
          return;
        }

        try {
          setIsImproveVoiceTranscribing(true);
          const payload = new FormData();
          payload.append('audio', blob, 'resume-improve-voice.webm');

          const response = await fetch('/api/voice/chat', {
            method: 'POST',
            body: payload,
          });

          if (!response.ok) {
            throw new Error(await response.text());
          }

          const data = (await response.json()) as { transcript?: string };
          const transcript = data.transcript?.trim();
          if (!transcript) {
            throw new Error('No transcript returned');
          }

          await handleImproveResume(transcript);
        } catch (error) {
          console.error('Failed to transcribe improve voice input:', error);
          setImproveVoiceError('Could not transcribe voice input. Please try again.');
        } finally {
          setIsImproveVoiceTranscribing(false);
        }
      };

      recorder.start();
      setIsImproveVoiceRecording(true);
    } catch (error) {
      console.error('Failed to start improve voice recording:', error);
      setImproveVoiceError('Unable to access microphone. Please check permissions.');
      cleanupImproveVoiceVisualization();
    }
  };

  const handleImproveResume = async (instructions?: string) => {
    if (!resumeId) return;

    setIsImproving(true);
    try {
      const trimmedInstructions = instructions?.trim();
      const response = await fetch(`/api/resume-builder/${resumeId}/improve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          trimmedInstructions
            ? { instructions: trimmedInstructions }
            : {}
        ),
      });

      if (response.ok) {
        const payload = (await response.json().catch(() => null)) as
          | { changes?: ResumeChangeItem[]; versionSnapshot?: { created?: boolean } | null }
          | null;
        await loadResume(resumeId);
        const snapshotted = payload?.versionSnapshot?.created === true;
        toast({
          title: 'Success!',
          description: snapshotted
            ? 'Resume improved. The previous version was saved to history.'
            : 'Resume improved with a stronger, human-sounding polish.',
        });
        setShowImproveDialog(false);
        setImproveInstructions('');
        setImproveVoiceError(null);
        const improveChanges = Array.isArray(payload?.changes) ? payload.changes : [];
        if (improveChanges.length > 0) {
          setChangeReview({
            title: 'What improve changed',
            description: snapshotted
              ? 'The improved resume is saved and loaded in the editor. The version before this run is in version history.'
              : 'The improved resume is already saved and loaded in the editor. This is what changed.',
            changes: improveChanges,
          });
        }
        return;
      }

      const error = await response.json().catch(() => ({ error: 'Failed to improve resume' }));
      toast({
        title: 'Error',
        description: error.error || 'Failed to improve resume',
        variant: 'destructive',
      });
    } catch (error) {
      console.error('Failed to improve resume:', error);
      toast({
        title: 'Error',
        description: 'An error occurred while improving the resume.',
        variant: 'destructive',
      });
    } finally {
      setIsImproving(false);
    }
  };

  /**
   * Applies one analysis suggestion straight from its inline comment popover
   * (the toolbar score's Annotate view, or a marker in the form/preview).
   *
   * Mirrors `handleApplySuggestion` in ResumeAnalyzeDialog.tsx — same
   * target-to-field mapping, same improve endpoint — but scoped to a single
   * suggestion with no comparison dialog, since this is a lighter-weight
   * touch-up path than the full analysis report.
   */
  const handleApplyInlineSuggestion = useCallback(
    async (suggestion: ResumeAnalysisSuggestion) => {
      const currentResume = useResumeBuilderStore.getState().resume;
      if (!currentResume || !resumeId || !suggestion.canAutoApply) return;

      let currentText = '';
      let field: 'summary' | 'responsibilities' | 'custom' | null = null;
      let context: { jobTitle?: string; company?: string } | undefined;
      let applyImprovedText: ((improvedText: string) => void) | null = null;
      const target = suggestion.target;

      switch (target.type) {
        case 'summary':
          currentText = currentResume.content.profile.summary || '';
          field = 'summary';
          applyImprovedText = (improvedText) => {
            updateContent({
              profile: {
                ...currentResume.content.profile,
                summary: improvedText,
              },
            });
          };
          break;
        case 'employment': {
          const employment = currentResume.content.employment.find(
            (item) => item.id === target.id
          );
          if (employment) {
            currentText = employment.responsibilities || '';
            field = 'responsibilities';
            context = {
              jobTitle: employment.title,
              company: employment.company,
            };
            applyImprovedText = (improvedText) => {
              updateEmployment(employment.id, { responsibilities: improvedText });
            };
          }
          break;
        }
        case 'customSection': {
          const section = currentResume.content.customSections.find(
            (item) => item.id === target.id
          );
          if (section) {
            currentText = section.content || '';
            field = 'custom';
            applyImprovedText = (improvedText) => {
              updateCustomSection(section.id, { content: improvedText });
            };
          }
          break;
        }
        case 'manual':
        default:
          break;
      }

      if (!field || !applyImprovedText || !currentText.trim()) {
        toast({
          title: 'Cannot auto-apply this fix',
          description:
            'This suggestion needs a manual edit or the target block is currently empty.',
          variant: 'destructive',
        });
        return;
      }

      setApplyingSuggestionId(suggestion.id);
      try {
        const response = await fetch('/api/resume-builder/improve', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            text: currentText,
            field,
            instructions: suggestion.instruction,
            context,
          }),
        });

        if (!response.ok) {
          const payload = (await response.json().catch(() => null)) as { error?: string } | null;
          throw new Error(payload?.error || 'Failed to apply improvement');
        }

        const payload = (await response.json()) as { improvedText?: string };
        const improvedText = payload.improvedText?.trim();
        if (!improvedText) {
          throw new Error('No improved text returned');
        }

        applyImprovedText(improvedText);
        await saveResume();

        const latestState = useResumeBuilderStore.getState();
        if (latestState.hasUnsavedChanges) {
          throw new Error(latestState.error || 'Failed to save the improved resume.');
        }

        toast({
          title: 'Suggestion applied',
          description: 'The resume was updated.',
        });

        refreshAnalysisScore(resumeId);
      } catch (applyError) {
        const message =
          applyError instanceof Error
            ? applyError.message
            : 'Failed to apply the suggested improvement';
        toast({
          title: 'Apply improvement failed',
          description: message,
          variant: 'destructive',
        });
      } finally {
        setApplyingSuggestionId(null);
      }
    },
    [resumeId, saveResume, toast, updateContent, updateCustomSection, updateEmployment, refreshAnalysisScore]
  );

  useEffect(() => {
    return () => {
      if (improveVoiceRecorderRef.current?.state === 'recording') {
        cancelImproveVoiceRef.current = true;
        improveVoiceRecorderRef.current.stop();
      }
      cleanupImproveVoiceVisualization();
      if (improveVoiceStreamRef.current) {
        improveVoiceStreamRef.current.getTracks().forEach((track) => track.stop());
        improveVoiceStreamRef.current = null;
      }
    };
  }, [cleanupImproveVoiceVisualization]);

  const handleNavigateBack = () => {
    if (window.history.length > 1) {
      router.back();
      return;
    }

    router.push(guestMode ? '/resume-builder' : '/d/pitch/resumes');
  };

  // Every authenticated host of this component (the /d and /dashboard route
  // groups) renders its own ~64px header above {children} and reserves space
  // for it, so subtracting 64px here fills exactly the rest of the viewport.
  // Guest mode has no such header — Layout.tsx excludes /resume-builder/create
  // Both guest and authenticated modes have a header above (site header or
  // dashboard header), so subtract the header height in both cases.
  const rootHeightClass = 'h-[calc(100vh-4.5rem)]';

  // In guest mode the resume is set synchronously from localStorage/defaults,
  // so the store's `isLoading` should never be true. Skip the loading gate.
  if (isLoading && !guestMode) {
    return (
      <div className={cn('flex items-center justify-center', rootHeightClass)}>
        <MainLoader title="Loading resume builder..." size="md" />
      </div>
    );
  }

  if (isRefinePreparing && !resumeId) {
    return (
      <div className={cn('flex items-center justify-center', rootHeightClass)}>
        <div className="rounded-md border border-border bg-card px-6 py-5">
          <MainLoader title="Preparing refined resume..." size="md" />
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col', rootHeightClass)}>
      {/* Header */}
      {isDesktop ? (
        <div className="flex items-center gap-2 border-b px-4 py-2">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleNavigateBack}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <div className="h-4 w-px bg-border" />
            {guestMode ? (
              <div className="min-w-0 flex-1 flex items-center gap-2 px-2">
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate text-body">{resume?.name || 'Resume Builder'}</span>
              </div>
            ) : (
            <div className="min-w-0">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 max-w-[220px] gap-1.5 px-2"
                    title={resume?.name || 'Select Resume'}
                  >
                    <FileText className="h-4 w-4 shrink-0" />
                    <span className="truncate">{resume?.name || 'Select Resume'}</span>
                    <ChevronDown className="h-4 w-4 shrink-0" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-60 max-w-[calc(100vw-2rem)]">
                  <DropdownMenuItem onClick={() => setShowNewResumeDialog(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    New Resume
                  </DropdownMenuItem>
                  {orderedResumes.length > 0 && (
                    <>
                      <div className="my-1 border-t" />
                      <div className="max-h-80 overflow-y-auto pr-1">
                        {orderedResumes.map((r) => {
                          const isCurrent = r.id === currentResumeId;
                          return (
                            <DropdownMenuItem
                              key={r.id}
                              onClick={() => handleSelectResume(r.id)}
                              selected={isCurrent}
                              className="items-start"
                            >
                              <FileText className="mr-2 mt-0.5 h-4 w-4 shrink-0" />
                              <div className="flex min-w-0 flex-1 items-start gap-2">
                                <div className="min-w-0 flex-1">
                                  <p className="whitespace-normal break-words">{r.name}</p>
                                  {isCurrent && (
                                    <p className="mt-0.5 text-caption opacity-75">Current resume</p>
                                  )}
                                </div>
                                <div className="ml-2 flex shrink-0 items-center gap-2">
                                  {r.version > 1 && (
                                        <span className={isCurrent ? 'text-caption opacity-75' : 'text-caption text-muted-foreground'}>
                                          v{r.version}
                                        </span>
                                      )}
                                  {isCurrent && <Check className="h-4 w-4" />}
                                </div>
                              </div>
                            </DropdownMenuItem>
                          );
                        })}
                      </div>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            )}

            {resume && (
              <>
                <div className="h-4 w-px bg-border" />
                <StyleSelector />
                <div className="h-4 w-px bg-border" />
                {/*
                  One entry point instead of four peer buttons.

                  These are not interchangeable modes, so they are a MENU rather
                  than tabs: you pick a tool and get the screen its input
                  actually needs. The labels describe the outcome, because
                  "Autofill" / "Refine" / "Improve" told the user nothing about
                  which one to press.
                */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={isActionBusy || isAnalyzeGuardBusy}
                      className="h-8 gap-1.5 px-2"
                    >
                      {isAiToolRunning ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Sparkles className="h-4 w-4" />
                      )}
                      <span>Toolbox</span>
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-72">
                    {AI_TOOLS.map((tool) => (
                      <DropdownMenuItem
                        key={tool.key}
                        onSelect={() => openAiTool(tool.key)}
                        className="items-start gap-2.5 py-2"
                      >
                        <tool.icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="min-w-0">
                          <span className="block text-label text-foreground">{tool.label}</span>
                          <span className="block text-caption text-muted-foreground">
                            {tool.description}
                          </span>
                        </span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                <div className="h-4 w-px bg-border" />
                {/*
                  Smart primary action: when the resume is mostly empty
                  (< 3 fields filled), the primary call-to-action is
                  "Fill in from" with a dropdown of fill sources. Once
                  the resume has content, switch to the Analyze button.
                */}
                {countFilledResumeFields(resume) < 3 ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={isActionBusy}
                        className="h-8 gap-1.5 px-2"
                      >
                        <Sparkles className="h-4 w-4" />
                        <span>Fill in from</span>
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-64">
                      <DropdownMenuItem
                        onSelect={() => openAiTool('autofill')}
                        className="items-start gap-2.5 py-2"
                      >
                        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="min-w-0">
                          <span className="block text-label text-foreground">Existing resume</span>
                          <span className="block text-caption text-muted-foreground">
                            Copy details from a resume you already have.
                          </span>
                        </span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => openAiTool('linkedin-fill')}
                        className="items-start gap-2.5 py-2"
                      >
                        <Linkedin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="min-w-0">
                          <span className="block text-label text-foreground">LinkedIn</span>
                          <span className="block text-caption text-muted-foreground">
                            Look up your LinkedIn profile and import your details.
                          </span>
                        </span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : isLoadingAnalysisScore ? (
                  <div className="flex h-8 items-center gap-1.5 px-2">
                    <div className="h-4 w-16 animate-pulse rounded bg-muted" />
                  </div>
                ) : isAnalysisInProgress ? (
                  <button
                    type="button"
                    onClick={handleOpenAnalyzeDialog}
                    className="flex h-8 items-center gap-1.5 rounded-md px-2 text-muted-foreground hover:bg-muted/50"
                  >
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-label">Analyzing</span>
                  </button>
                ) : latestAnalysisScore !== null ? (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={handleOpenAnalyzeDialog}
                      disabled={isActionBusy || isAnalyzeGuardBusy}
                      className="flex h-8 items-center rounded-md px-1.5 hover:bg-muted/50 disabled:opacity-60"
                      title="Open analysis report"
                    >
                      <ScoreRing score={latestAnalysisScore} size="xs" />
                    </button>
                    {analysisSuggestions.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setShowAnalysisComments((prev) => !prev)}
                        className={cn(
                          'flex h-8 items-center gap-1 rounded-md px-2 text-label transition-colors hover:bg-muted/50',
                          showAnalysisComments ? 'text-primary' : 'text-muted-foreground'
                        )}
                        title={showAnalysisComments ? 'Hide annotations' : 'Show annotations'}
                      >
                        <MessageSquareText className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">Annotate</span>
                      </button>
                    )}
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleOpenAnalyzeDialog}
                    disabled={isActionBusy || isAnalyzeGuardBusy}
                    className="h-8 gap-1.5 px-2"
                  >
                    <BarChart3 className="h-4 w-4" />
                    <span>Analyze</span>
                  </Button>
                )}
              </>
            )}
          </div>

          <div className="h-4 w-px bg-border shrink-0 mx-1" />

          <div className="ml-auto flex items-center gap-2 shrink-0">
            {lastSaved && !hasUnsavedChanges && !guestMode && (
              <span className="hidden 2xl:flex items-center gap-1 text-caption text-muted-foreground whitespace-nowrap">
                <Clock className="h-3 w-3" />
                {formatLastSaved()}
              </span>
            )}

            {shouldShowManualSaveFallback && !guestMode && (
              <Button variant="outline" size="sm" onClick={() => saveResume()} disabled={isSaving}>
                {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Save
              </Button>
            )}

            {resume && (
              <>
                {/*
                  Same buttons in guest mode as authenticated — version history
                  and export both need a server-side resume, so each action is
                  gated individually rather than hiding the whole section.
                */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 gap-1.5 px-2">
                      <Clock className="h-4 w-4" />
                      <span>Version {resume.version}</span>
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => (guestMode ? withGuestGate() : handleSaveAsVersion())}>
                      Save as new version
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => (guestMode ? withGuestGate() : setShowVersionHistoryDialog(true))}>
                      Version history
                    </DropdownMenuItem>
                    {!guestMode && resume.originalSourceId && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={handleViewOriginal}>
                          View original
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button
                  size="sm"
                  onClick={() => (guestMode ? void handleGuestShare() : setShowShareDialog(true))}
                  disabled={guestMode && isPreparingGuestShare}
                >
                  {guestMode && isPreparingGuestShare ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Share2 className="mr-2 h-4 w-4" />
                  )}
                  Share &amp; export
                </Button>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleNavigateBack}
            aria-label="Back"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          {resume && (
            <div className="ml-auto flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowMobilePreviewDialog(true)}
                className="gap-1.5 px-3"
              >
                <Eye className="h-4 w-4" />
                Preview
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={guestMode ? () => void handleGuestShare() : () => setShowShareDialog(true)}
                disabled={guestMode && isPreparingGuestShare}
                className="hidden min-[410px]:inline-flex gap-1.5 px-3"
              >
                {guestMode && isPreparingGuestShare ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Share2 className="h-4 w-4" />
                )}
                Share
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowMobileMenuDialog(true)}
                aria-label="Open tools and more"
                className="gap-1.5 px-3"
              >
                Tools
                <MoreVertical className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}

      {shouldShowRefineHint && (
        <div className="border-b border-border bg-primary/5 px-4 py-2 text-caption text-foreground">
          Refinement mode is active. When your refined resume is ready, you can close this tab and select it in your previous workflow.
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="border-b border-red-200 bg-red-50 px-4 py-2 text-body text-red-600">
          {error}
        </div>
      )}

      {/* Main content */}
      {resume ? (
        isDesktop ? (
          <div className="flex flex-1 overflow-hidden">
            <div className="w-1/2 overflow-y-auto border-r">
              <ResumeForm
                guestMode={guestMode}
                onGuestAction={withGuestGate}
                analysisSuggestions={analysisSuggestions}
                showAnalysisComments={showAnalysisComments}
                onApplySuggestion={handleApplyInlineSuggestion}
                applyingSuggestionId={applyingSuggestionId}
              />
            </div>
            <div className="w-1/2 overflow-y-auto bg-gray-100">
              <ResumePreview
                analysisSuggestions={analysisSuggestions}
                showAnalysisComments={showAnalysisComments}
                onApplySuggestion={handleApplyInlineSuggestion}
                applyingSuggestionId={applyingSuggestionId}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-1 overflow-hidden">
            <div className="w-full overflow-y-auto">
              <ResumeForm
                guestMode={guestMode}
                onGuestAction={withGuestGate}
                analysisSuggestions={analysisSuggestions}
                showAnalysisComments={showAnalysisComments}
                onApplySuggestion={handleApplyInlineSuggestion}
                applyingSuggestionId={applyingSuggestionId}
              />
            </div>
          </div>
        )
      ) : (
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-2xl px-4 py-6 sm:px-6 sm:py-10">
            <div className="px-6 py-5 space-y-6 bg-card border border-border" style={{ boxShadow: 'var(--shadow-warm-sm)' }}>
              {loadingResumes ? (
                <div className="space-y-2" aria-label="Loading resumes">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <div key={index} className="h-16 animate-pulse rounded-lg bg-muted/40" />
                  ))}
                </div>
              ) : (
              <>
              {/* Action tiles */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setEmptyStateMode((prev) => (prev === 'create' ? 'idle' : 'create'))}
                  className={cn(
                    'flex flex-col items-start gap-3 p-4 border transition-all text-left',
                    emptyStateMode === 'create'
                      ? 'border-primary bg-primary/5'
                      : 'border-border bg-card hover:border-primary/40 hover:bg-primary/[0.03]'
                  )}
                >
                  <div
                    className={cn(
                      'flex h-9 w-9 items-center justify-center border',
                      emptyStateMode === 'create'
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-background text-muted-foreground'
                    )}
                  >
                    <Plus className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-foreground">Create New</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Start from a blank resume</p>
                  </div>
                </button>

                <a
                  href="/d/pitch/resumes?upload=1"
                  className="flex flex-col items-start gap-3 p-4 border border-border bg-card hover:border-primary/40 hover:bg-primary/[0.03] transition-all text-left"
                >
                  <div className="flex h-9 w-9 items-center justify-center border border-border bg-background text-muted-foreground">
                    <Upload className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-foreground">Upload Resume</p>
                    <p className="text-xs text-muted-foreground mt-0.5">PDF, DOCX, or TXT · max 10MB</p>
                  </div>
                </a>
              </div>

              {/* Create form */}
              {emptyStateMode === 'create' && (
                <div className="space-y-3 border border-primary/30 bg-primary/[0.03] p-4">
                  <label className="text-xs font-bold uppercase tracking-[0.1em] text-foreground">
                    Resume Name
                  </label>
                  <Input
                    value={newResumeName}
                    onChange={(e) => setNewResumeName(e.target.value)}
                    placeholder="e.g., Senior Product Engineer Resume"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void handleCreateResume();
                    }}
                  />
                  <Button
                    onClick={() => void handleCreateResume()}
                    disabled={!newResumeName.trim() || isCreating}
                    className="w-full font-bold uppercase tracking-tight"
                  >
                    {isCreating ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating…
                      </>
                    ) : (
                      'Create Resume'
                    )}
                  </Button>
                </div>
              )}

              {/* Existing resumes */}
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground mb-3">
                  Existing Resumes
                </p>
                {builderResumeItems.length === 0 ? (
                  <div className="border border-dashed border-border py-8 text-center">
                    <FileText className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">
                      No resumes yet. Create one above.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {emptyStateVisible.map((r) => (
                      <ResumeListItem
                        key={r.id}
                        resume={r}
                        isSelected={r.id === currentResumeId}
                        showRefineButton={false}
                        showDeleteButton={false}
                        showViewButton={false}
                        onSelect={() => handleSelectResume(r.id)}
                      />
                    ))}
                    {emptyStateTotalPages > 1 && (
                      <div className="flex items-center justify-between pt-2">
                        <p className="text-xs text-muted-foreground">
                          {emptyStateClampedPage * BUILDER_PAGE_SIZE + 1}–
                          {Math.min((emptyStateClampedPage + 1) * BUILDER_PAGE_SIZE, builderResumeItems.length)} of {builderResumeItems.length}
                        </p>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="border"
                            disabled={emptyStateClampedPage === 0}
                            onClick={() => setEmptyStatePage((p) => p - 1)}
                          >
                            <ChevronLeft className="h-4 w-4" />
                            Prev
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="border"
                            disabled={emptyStateClampedPage >= emptyStateTotalPages - 1}
                            onClick={() => setEmptyStatePage((p) => p + 1)}
                          >
                            Next
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              </>
              )}
            </div>
          </div>
        </div>
      )}

      {resume && (
        <Dialog open={showMobilePreviewDialog} onOpenChange={setShowMobilePreviewDialog}>
          <DialogContent className="left-0 top-0 h-[100dvh] w-screen max-w-none translate-x-0 translate-y-0 rounded-none border-0 p-0 sm:rounded-none [&>button]:hidden">
            <div className="flex h-full min-h-0 flex-col">
              <div className="flex items-center gap-2 border-b px-3 py-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowMobilePreviewDialog(false)}
                  aria-label="Close preview"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-title-sm">{resume.name}</p>
                  <p className="text-caption text-muted-foreground">Preview</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowMobilePreviewDialog(false)}
                  aria-label="Close preview"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex-1 overflow-hidden bg-gray-100">
                <ResumePreview
                  defaultToFit
                  controlsFixedToViewport
                  analysisSuggestions={analysisSuggestions}
                  showAnalysisComments={showAnalysisComments}
                  onApplySuggestion={handleApplyInlineSuggestion}
                  applyingSuggestionId={applyingSuggestionId}
                />
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={showMobileMenuDialog} onOpenChange={setShowMobileMenuDialog}>
        <DialogContent className="left-0 top-0 h-[100dvh] w-screen max-w-none translate-x-0 translate-y-0 rounded-none border-0 p-0 sm:rounded-none [&>button]:hidden">
          <div className="flex h-full min-h-0 flex-col">
            <div className="flex items-center gap-2 border-b px-3 py-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowMobileMenuDialog(false)}
                aria-label="Close menu"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="min-w-0 flex-1">
                <p className="truncate text-title-sm">{resume?.name || 'Resume Menu'}</p>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 pb-28">
              <div className="space-y-5">
                {!guestMode && (
                <section className="space-y-2">
                  <p className="text-overline text-muted-foreground">Resume</p>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-between"
                        title={resume?.name || 'Select Resume'}
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          <FileText className="h-4 w-4 shrink-0" />
                          <span className="truncate">{resume?.name || 'Select Resume'}</span>
                        </span>
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-[calc(100vw-2rem)] max-w-sm">
                      <DropdownMenuItem
                        onClick={() => {
                          setShowMobileMenuDialog(false);
                          setShowNewResumeDialog(true);
                        }}
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        New Resume
                      </DropdownMenuItem>
                      {orderedResumes.length > 0 && (
                        <>
                          <div className="my-1 border-t" />
                          <div className="max-h-80 overflow-y-auto pr-1">
                            {orderedResumes.map((r) => {
                              const isCurrent = r.id === currentResumeId;
                              return (
                                <DropdownMenuItem
                                  key={r.id}
                                  onClick={() => {
                                    setShowMobileMenuDialog(false);
                                    handleSelectResume(r.id);
                                  }}
                                  selected={isCurrent}
                                  className="items-start"
                                >
                                  <FileText className="mr-2 mt-0.5 h-4 w-4 shrink-0" />
                                  <div className="flex min-w-0 flex-1 items-start gap-2">
                                    <div className="min-w-0 flex-1">
                                      <p className="whitespace-normal break-words">{r.name}</p>
                                      {isCurrent && (
                                        <p className="mt-0.5 text-caption opacity-75">Current resume</p>
                                      )}
                                    </div>
                                    <div className="ml-2 flex shrink-0 items-center gap-2">
                                      {r.version > 1 && (
                                        <span className={isCurrent ? 'text-caption opacity-75' : 'text-caption text-muted-foreground'}>
                                          v{r.version}
                                        </span>
                                      )}
                                      {isCurrent && <Check className="h-4 w-4" />}
                                    </div>
                                  </div>
                                </DropdownMenuItem>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </section>
                )}

                {resume && (
                  <section className="space-y-2">
                    <p className="text-overline text-muted-foreground">Style</p>
                    <div className="rounded-md border border-border p-1">
                      <StyleSelector />
                    </div>
                  </section>
                )}

                {resume && (
                  <section className="space-y-2">
                    <p className="text-overline text-muted-foreground">Tools</p>
                    {/* Same table as the desktop menu, so the two never drift apart. */}
                    {AI_TOOLS.map((tool) => (
                      <Button
                        key={tool.key}
                        variant="outline"
                        onClick={() => {
                          setShowMobileMenuDialog(false);
                          openAiTool(tool.key);
                        }}
                        disabled={isActionBusy || (tool.key === 'analyze' && isAnalyzeGuardBusy)}
                        className="h-auto w-full justify-start py-2 text-left"
                      >
                        <tool.icon className="mr-2 mt-0.5 h-4 w-4 shrink-0" />
                        <span className="min-w-0 flex-1">
                          <span className="block whitespace-normal normal-case text-label">{tool.label}</span>
                          <span className="block whitespace-normal normal-case text-caption text-muted-foreground">
                            {tool.description}
                          </span>
                        </span>
                      </Button>
                    ))}
                    {analysisSuggestions.length > 0 && (
                      <Button
                        variant={showAnalysisComments ? 'default' : 'outline'}
                        onClick={() => setShowAnalysisComments((prev) => !prev)}
                        className="h-auto w-full justify-start py-2 text-left"
                      >
                        <MessageSquareText className="mr-2 mt-0.5 h-4 w-4 shrink-0" />
                        <span className="min-w-0 flex-1">
                          <span className="block whitespace-normal normal-case text-label">
                            {showAnalysisComments ? 'Hide annotations' : 'Show annotations'}
                          </span>
                          <span className="block whitespace-normal normal-case text-caption text-muted-foreground">
                            {analysisSuggestions.length} suggestion{analysisSuggestions.length === 1 ? '' : 's'} from the latest analysis.
                          </span>
                        </span>
                      </Button>
                    )}
                  </section>
                )}

                {resume && (
                  <section className="space-y-2">
                    <p className="text-overline text-muted-foreground">Version</p>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="w-full justify-start">
                          <ChevronDown className="mr-2 h-4 w-4" />
                          Version {resume.version}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-56 max-w-[calc(100vw-2rem)]">
                        <DropdownMenuItem
                          onClick={() => {
                            setShowMobileMenuDialog(false);
                            if (guestMode) { withGuestGate(); return; }
                            void handleSaveAsVersion();
                          }}
                        >
                          Save as new version
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            setShowMobileMenuDialog(false);
                            if (guestMode) { withGuestGate(); return; }
                            setShowVersionHistoryDialog(true);
                          }}
                        >
                          Version history
                        </DropdownMenuItem>
                        {!guestMode && resume.originalSourceId && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => {
                                setShowMobileMenuDialog(false);
                                handleViewOriginal();
                              }}
                            >
                              View original
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </section>
                )}
              </div>
            </div>

            {resume && (
              <div className="border-t bg-background px-4 py-3">
                <div className="flex items-center gap-2">
                  <Button
                    onClick={
                      guestMode
                        ? () => { setShowMobileMenuDialog(false); void handleGuestShare(); }
                        : () => { setShowMobileMenuDialog(false); setShowShareDialog(true); }
                    }
                    disabled={guestMode && isPreparingGuestShare}
                    className="flex-1"
                  >
                    {guestMode && isPreparingGuestShare ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Share2 className="mr-2 h-4 w-4" />
                    )}
                    Share &amp; export
                  </Button>
                </div>
                {shouldShowManualSaveFallback && !guestMode && (
                  <Button
                    variant="outline"
                    className="mt-2 w-full"
                    onClick={() => saveResume()}
                    disabled={isSaving}
                  >
                    {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                    Save
                  </Button>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* New Resume Dialog */}
      <Dialog open={showNewResumeDialog} onOpenChange={setShowNewResumeDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto gap-0 p-0">
          <DialogHeading
            title="My Resumes"
            description="Create a blank resume or upload your existing one. Your resumes are listed below."
          />

          <div className="px-6 py-5 space-y-6">
            {loadingResumes ? (
              <div className="space-y-2" aria-label="Loading resumes">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="h-16 animate-pulse rounded-lg bg-muted/40" />
                ))}
              </div>
            ) : (
            <>
            {/* Action tiles */}
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setDialogStartMode((prev) => (prev === 'create' ? 'idle' : 'create'))}
                className={cn(
                  'flex flex-col items-start gap-3 p-4 border transition-all text-left',
                  dialogStartMode === 'create'
                    ? 'border-primary bg-primary/5'
                    : 'border-border bg-card hover:border-primary/40 hover:bg-primary/[0.03]'
                )}
              >
                <div
                  className={cn(
                    'flex h-9 w-9 items-center justify-center border',
                    dialogStartMode === 'create'
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-background text-muted-foreground'
                  )}
                >
                  <Plus className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-bold text-foreground">Create New</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Start from a blank resume</p>
                </div>
              </button>

              <a
                href="/d/pitch/resumes?upload=1"
                className="flex flex-col items-start gap-3 p-4 border border-border bg-card hover:border-primary/40 hover:bg-primary/[0.03] transition-all text-left"
              >
                <div className="flex h-9 w-9 items-center justify-center border border-border bg-background text-muted-foreground">
                  <Upload className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-bold text-foreground">Upload Resume</p>
                  <p className="text-xs text-muted-foreground mt-0.5">PDF, DOCX, or TXT · max 10MB</p>
                </div>
              </a>
            </div>

            {/* Create form */}
            {dialogStartMode === 'create' && (
              <div className="space-y-3 border border-primary/30 bg-primary/[0.03] p-4">
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-foreground">
                  Resume Name
                </label>
                <Input
                  value={newResumeName}
                  onChange={(e) => setNewResumeName(e.target.value)}
                  placeholder="e.g., Senior Product Engineer Resume"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void handleCreateResume();
                  }}
                />
                <Button
                  onClick={() => void handleCreateResume()}
                  disabled={!newResumeName.trim() || isCreating}
                  className="w-full font-bold uppercase tracking-tight"
                >
                  {isCreating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating…
                    </>
                  ) : (
                    'Create Resume'
                  )}
                </Button>
              </div>
            )}

            {/* Existing resumes */}
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground mb-3">
                Existing Resumes
              </p>
              {builderResumeItems.length === 0 ? (
                <div className="border border-dashed border-border py-8 text-center">
                  <FileText className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    No resumes yet. Create one above.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {dialogVisible.map((r) => (
                    <ResumeListItem
                      key={r.id}
                      resume={r}
                      isSelected={r.id === currentResumeId}
                      showRefineButton={false}
                      showDeleteButton={false}
                      showViewButton={false}
                      onSelect={() => handleSelectResume(r.id)}
                    />
                  ))}
                  {dialogTotalPages > 1 && (
                    <div className="flex items-center justify-between pt-2">
                      <p className="text-xs text-muted-foreground">
                        {dialogClampedPage * BUILDER_PAGE_SIZE + 1}–
                        {Math.min((dialogClampedPage + 1) * BUILDER_PAGE_SIZE, builderResumeItems.length)} of {builderResumeItems.length}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="border"
                          disabled={dialogClampedPage === 0}
                          onClick={() => setDialogPage((p) => p - 1)}
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Prev
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="border"
                          disabled={dialogClampedPage >= dialogTotalPages - 1}
                          onClick={() => setDialogPage((p) => p + 1)}
                        >
                          Next
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {resume && (
        guestMode ? (
          <GuestResumeShareDialog
            open={showShareDialog}
            onOpenChange={setShowShareDialog}
            resumeName={resume.name}
            shareUrl={guestSyncToken ? buildSharedResumePath(guestSyncToken) : null}
            token={guestSyncToken}
            onSignIn={() => withGuestGate()}
            onGuestExport={() => withGuestGate()}
          />
        ) : (
          <ResumeShareExportDialog
            open={showShareDialog}
            onOpenChange={setShowShareDialog}
            resumeId={resume.id}
            resumeName={resume.name}
            initialShare={resume.share}
            onShareChange={(share) => updateShare(share)}
          />
        )
      )}

      {/* LinkedIn fill — works in both guest and authenticated mode. */}
      {resume && (
        <LinkedInFillDialog
          open={showLinkedInFillDialog}
          onOpenChange={setShowLinkedInFillDialog}
          onFill={handleLinkedInFill}
        />
      )}

      {/* Autofill — resume source only. */}
      {resume && (
        <AutofillSourceDialog
          open={showAutofillDialog}
          onOpenChange={setShowAutofillDialog}
          onSaveSource={handleSubmitSource}
          onRunAutofill={runFillFromSources}
          // Load-bearing: runFillFromSources reads `sources.length` from its own
          // closure, so on a first-ever autofill the run must wait for the saved
          // source to land in state or it self-rejects with "No sources".
          sourceCount={sources.length}
          isRunningAutofill={isFillingFromSources}
        />
      )}

      {/* Refine — job source only, manual entry first. */}
      {resume && (
        <RefineJobSourceDialog
          open={showRefineDialog}
          onOpenChange={setShowRefineDialog}
          onAddSource={handleSubmitSource}
          onRefine={runRefine}
          isRefining={isRefining}
          progressSlot={
            <ResumeRefineProgress
              steps={refineAgent.steps}
              status={refineAgent.status}
              error={refineAgent.error}
              trace={refineAgent.trace}
            />
          }
        />
      )}

      {/* Improve Dialog */}
      {resume && (
        <Dialog open={showImproveDialog} onOpenChange={handleImproveDialogOpenChange}>
          <DialogContent className="max-w-lg">
            <DialogHeading
              title={<><PenLine className="mr-2 inline h-5 w-5" />Improve Resume</>}
              description="This runs a full-pass polish to tighten wording, strengthen outcomes, and improve flow while keeping your facts intact. It avoids generic AI tone and aims to read like a carefully edited human-written resume."
            />
            <div className="space-y-3 py-2">
              <div className="space-y-1">
                <p className="text-label">Optional instructions</p>
                <p className="text-caption text-muted-foreground">
                  Add any specific direction for this pass, or leave blank.
                </p>
              </div>
              <Textarea
                value={improveInstructions}
                onChange={(event) => setImproveInstructions(event.target.value)}
                placeholder="Example: Prioritize product leadership impact and tighten bullet verbs."
                rows={4}
                disabled={isImproving || isImproveVoiceRecording || isImproveVoiceTranscribing}
              />
              <div className="rounded-md border border-border bg-muted/30 px-3 py-3">
                {/*
                  Stacked, not side-by-side. The old row gave the copy 60% and
                  squeezed the controls into the rest, so "Stop & apply" and
                  "Cancel" wrapped onto separate lines. Title, then description,
                  then one full-width row for the controls.
                */}
                <div className="space-y-1">
                  <p className="text-label">Voice mode (optional)</p>
                  <p className="text-caption text-muted-foreground">
                    Record and stop to auto-run improve with your dictated instructions.
                  </p>
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
                {isImproveVoiceRecording ? (
                    <>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => stopImproveVoiceRecording(true)}
                      disabled={isImproving || isImproveVoiceTranscribing}
                      className="h-9 px-3"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => stopImproveVoiceRecording(false)}
                      disabled={isImproving || isImproveVoiceTranscribing}
                      className="h-9 px-3"
                    >
                      <Square className="mr-1.5 h-3.5 w-3.5" />
                      Stop & apply
                    </Button>
                  </>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleStartImproveVoiceRecording}
                    disabled={isImproving || isImproveVoiceTranscribing}
                    className="h-9 px-3"
                  >
                    {isImproveVoiceTranscribing ? (
                      <>
                        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                        Transcribing...
                      </>
                    ) : (
                      <>
                        <Mic className="mr-1.5 h-3.5 w-3.5" />
                        Dictate
                      </>
                    )}
                  </Button>
                )}
                </div>
              </div>
              {(isImproveVoiceRecording || isImproveVoiceTranscribing) && (
                <div className="rounded-md border border-border/70 bg-background/80 px-3 py-2">
                  <VoiceWaveform
                    levels={improveVoiceLevels}
                    isActive={isImproveVoiceRecording}
                    minScale={0.25}
                  />
                  <p className="mt-2 text-caption text-muted-foreground">
                    {isImproveVoiceRecording ? 'Listening...' : 'Transcribing your instructions...'}
                  </p>
                </div>
              )}
              {improveVoiceError && (
                <p className="text-caption text-destructive">{improveVoiceError}</p>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => handleImproveDialogOpenChange(false)}
                disabled={isImproving || isImproveVoiceTranscribing}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleImproveResume(improveInstructions)}
                disabled={isImproving || isImproveVoiceRecording || isImproveVoiceTranscribing}
              >
                {isImproving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <PenLine className="mr-2 h-4 w-4" />
                )}
                Run improve
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {resume && (
        <ResumeAnalyzeDialog
          open={showAnalyzeDialog}
          onOpenChange={(open) => {
            setShowAnalyzeDialog(open);
            if (!open) {
              // A new analysis may have just completed inside the dialog —
              // pick up its score for the toolbar indicator.
              refreshAnalysisScore(resume.id);
            }
          }}
          resumeId={resume.id}
          resumeName={resume.name}
        />
      )}

      {/* Add Source Dialog */}
      {resume && (
        <AddSourceDialog
          key={addSourceInitialTab}
          open={showAddSourceDialog}
          onOpenChange={setShowAddSourceDialog}
          onAdd={handleSubmitSource}
          defaultTab={addSourceInitialTab}
        />
      )}

      {resume && (
        <ResumeVersionHistoryDialog
          open={showVersionHistoryDialog}
          onOpenChange={setShowVersionHistoryDialog}
          resumeId={resume.id}
          onRestored={async ({ changes, restoredLabel }) => {
            // The restore already wrote to the row; the editor is still holding
            // the content it replaced until this reload.
            await loadResume(resume.id);
            toast({
              title: 'Version restored',
              description: `Restored "${restoredLabel}". Your previous resume was saved to history first.`,
            });
            const restoredChanges = Array.isArray(changes) ? (changes as ResumeChangeItem[]) : [];
            if (restoredChanges.length > 0) {
              setChangeReview({
                title: 'What the rollback changed',
                description:
                  'The restored version is loaded in the editor. The resume you had before restoring is at the top of version history.',
                changes: restoredChanges,
              });
            }
          }}
        />
      )}

      <ResumeComparisonDialog
        open={Boolean(changeReview)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setChangeReview(null);
        }}
        title={changeReview?.title ?? 'What changed'}
        description={changeReview?.description}
        changes={changeReview?.changes ?? []}
        closeLabel="Done"
      />
    </div>
  );
}
